import React from 'react'
import './LearningTracker.css'

interface LearningStats {
  totalPredictions: number
  correctPredictions: number
  feedbackReceived: number
  modelVersion: string
  lastRetrained?: string
}

interface LearningTrackerProps {
  stats: LearningStats
  isVisible: boolean
}

const LearningTracker: React.FC<LearningTrackerProps> = ({ stats, isVisible }) => {
  if (!isVisible) return null

  const accuracy = stats.feedbackReceived > 0 
    ? Math.round((stats.correctPredictions / stats.feedbackReceived) * 100)
    : 0

  const feedbackRate = stats.totalPredictions > 0
    ? Math.round((stats.feedbackReceived / stats.totalPredictions) * 100)
    : 0

  const getAccuracyColor = (accuracy: number) => {
    if (accuracy >= 80) return '#4CAF50'
    if (accuracy >= 60) return '#FF9800'
    return '#f44336'
  }

  const getAccuracyEmoji = (accuracy: number) => {
    if (accuracy >= 90) return '🎯'
    if (accuracy >= 80) return '👍'
    if (accuracy >= 60) return '📈'
    return '🎲'
  }

  return (
    <div className="learning-tracker">
      <div className="tracker-header">
        <h3>🧠 AI Learning Progress</h3>
        <span className="model-version">Model v{stats.modelVersion}</span>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <div className="stat-number">{stats.totalPredictions}</div>
            <div className="stat-label">Total Predictions</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">💬</div>
          <div className="stat-content">
            <div className="stat-number">{stats.feedbackReceived}</div>
            <div className="stat-label">Feedback Received</div>
            <div className="stat-progress">
              <div 
                className="progress-bar"
                style={{ width: `${Math.min(feedbackRate, 100)}%` }}
              />
            </div>
          </div>
        </div>

        <div className="stat-card accuracy-card">
          <div className="stat-icon" style={{ color: getAccuracyColor(accuracy) }}>
            {getAccuracyEmoji(accuracy)}
          </div>
          <div className="stat-content">
            <div className="stat-number" style={{ color: getAccuracyColor(accuracy) }}>
              {accuracy}%
            </div>
            <div className="stat-label">Accuracy</div>
            <div className="accuracy-description">
              {accuracy >= 90 && "Excellent! 🏆"}
              {accuracy >= 80 && accuracy < 90 && "Very Good! 🌟"}
              {accuracy >= 60 && accuracy < 80 && "Improving 📈"}
              {accuracy < 60 && stats.feedbackReceived < 5 && "Learning... 🎲"}
              {accuracy < 60 && stats.feedbackReceived >= 5 && "Needs more data 📚"}
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-number">{stats.correctPredictions}</div>
            <div className="stat-label">Correct Predictions</div>
          </div>
        </div>
      </div>

      {stats.lastRetrained && (
        <div className="retrain-info">
          <span className="retrain-icon">🔄</span>
          <span>Last retrained: {stats.lastRetrained}</span>
        </div>
      )}

      <div className="learning-tips">
        <h4>💡 Help the AI Learn:</h4>
        <ul>
          <li>Provide feedback on predictions when you know the actual gender</li>
          <li>Upload clear, front-facing ultrasound scans (12-15 weeks optimal)</li>
          <li>The AI automatically retrains every 10 validated predictions</li>
          <li>More feedback = better accuracy for everyone!</li>
        </ul>
      </div>
    </div>
  )
}

export default LearningTracker